import { formatPersonName } from '@gb-ticket/shared-types';
import { useState, type ReactNode } from 'react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { UserAvatar } from '@/platform/components/user-avatar';
import { useVoice } from '@/platform/voice/use-voice';

type ProfilePhotoFields = {
  firstName: string;
  lastName: string;
  displayName: string;
  profilePhotoUrl?: string | null;
};

/** Large profile photo in a dialog (shared by profile page and intranet colleague sheet). */
export function ProfilePhotoZoomDialog({
  open,
  onOpenChange,
  name,
  profilePhotoUrl,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  profilePhotoUrl: string;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl gap-4 sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Profilfoto</DialogTitle>
        </DialogHeader>
        <img
          src={profilePhotoUrl}
          alt={`Profilfoto von ${name}`}
          className="mx-auto max-h-[min(70vh,32rem)] w-full rounded-lg object-contain"
        />
        <p className="text-center text-sm text-muted-foreground">{name}</p>
      </DialogContent>
    </Dialog>
  );
}

/** Wraps any avatar; opens zoom dialog when `profilePhotoUrl` is set. */
export function ZoomableProfilePhoto({
  name,
  profilePhotoUrl,
  className,
  children,
}: {
  name: string;
  profilePhotoUrl?: string | null;
  className?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const hasPhoto = Boolean(profilePhotoUrl);

  if (!hasPhoto) {
    return <div className={className}>{children}</div>;
  }

  return (
    <>
      <button
        type="button"
        className={cn(
          'rounded-full transition-opacity hover:opacity-90',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none',
          className,
        )}
        aria-label={`Profilfoto von ${name} vergrößern`}
        onClick={() => setOpen(true)}
      >
        {children}
      </button>
      <ProfilePhotoZoomDialog
        open={open}
        onOpenChange={setOpen}
        name={name}
        profilePhotoUrl={profilePhotoUrl!}
      />
    </>
  );
}

/** Opens a large preview when the person has uploaded a profile photo. */
export function ProfilePhotoPreview({
  profile,
  className,
  avatarClassName,
}: {
  profile: ProfilePhotoFields;
  className?: string;
  avatarClassName?: string;
}) {
  const voice = useVoice();
  const name = formatPersonName(profile, voice);
  const avatar = (
    <UserAvatar
      firstName={profile.firstName}
      lastName={profile.lastName}
      displayName={profile.displayName}
      profilePhotoUrl={profile.profilePhotoUrl}
      className={avatarClassName}
    />
  );

  return (
    <ZoomableProfilePhoto
      name={name}
      profilePhotoUrl={profile.profilePhotoUrl}
      className={cn('overflow-hidden rounded-full', className)}
    >
      {avatar}
    </ZoomableProfilePhoto>
  );
}
